import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_ID,
  EnvironmentProviders,
  Provider,
  importProvidersFrom,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import {
  AUTH_CONFIG,
  RECAPTCHA_OVERRIDE_TOKEN,
  provideAuth,
  provideRecaptchaTokenInterceptor,
  recaptchaTokenInterceptor,
} from '@domgen/dgx-fe-authentication';
import {
  CATALOGUE_CONFIG,
  CATALOGUE_FEATURE_KEY,
  provideCatalogue,
} from '@domgen/dgx-fe-catalogue';
import {
  CMS_CONFIG,
  CMS_CONTEXT,
  CMS_FEATURE_KEY,
  CmsConfig,
  cmsContextFactory,
  isInCmsEditorFactory,
  provideCms,
} from '@domgen/dgx-fe-cms';
import {
  BYPASS_ROUTE_GUARD,
  COMMON_CONFIG,
  COMMON_FEATURE_KEY,
  DOMAIN_ID,
  correlationIdInterceptor,
  formatPayloadInterceptor,
  provideCommonStore,
  requestActionInterceptor,
  requestSourceInterceptor,
  resetMetaReducer,
  sessionStorageSync,
  whiteLabelClientInterceptor,
} from '@domgen/dgx-fe-common';
import { CONFIG, ENVIRONMENT, provideConfig } from '@domgen/dgx-fe-config';
import { provideFormsAddressDefaults } from '@domgen/dgx-fe-forms/address';
import { FORM_CONFIG } from '@domgen/dgx-fe-forms/shared';
import {
  GA_CONFIG,
  GA_GLOBAL_DATA,
  gaHttpErrorInterceptor,
  provideGa,
} from '@domgen/dgx-fe-ga';
import { provideI18nDefaults } from '@domgen/dgx-fe-i18n';
import {
  LIGHTSTEP_CONFIG,
  provideLightstepInitializer,
} from '@domgen/dgx-fe-lightstep';
import {
  LoggingLevel,
  loggingInterceptor,
  provideLogging,
} from '@domgen/dgx-fe-logging';
import { provideNotificationsStore } from '@domgen/dgx-fe-notifications';
import { SEO_CONFIG, provideSeo } from '@domgen/dgx-fe-seo';
import { EditorContextService } from '@magnolia/angular-editor';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  myAccountInterceptor,
  provideSelfFixStore,
} from '@self-fix/data-access';
import { SELF_FIX_CONFIG } from '@self-fix/util-models';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { getAppRoutes } from './app.routes';
import { authConfigFactory } from './factories/auth-config/auth-config.factory';
import { catalogueConfigFactory } from './factories/catalogue-config/catalogue-config.factory';
import { commonConfigFactory } from './factories/common-config/common-config.factory';
import { domainIdFactory } from './factories/domain-id/domain-id.factory';
import { formConfigFactory } from './factories/form-config/form-config.factory';
import { lightstepConfigFactory } from './factories/lightstep-config/lightstep-config.factory';
import { recaptchaOverrideTokenFactory } from './factories/recaptcha-override-token/recaptcha-override-token.factory';
import { recaptchaSiteKeyFactory } from './factories/recaptcha-site-key/recaptcha-site-key.factory';
import { selfFixConfigFactory } from './factories/self-fix-config/self-fix-config.factory';
import { seoConfigFactory } from './factories/seo-config/seo-config.factory';
import { environment } from '../configs/environments/environment';

export function provideApp(): Provider | EnvironmentProviders[] {
  const extractDomain = (url: string) =>
    url.match(/^(?:https?:\/\/)?([^\/\?#]+)/i)?.[1];
  const referrerDomain = extractDomain(document.referrer);
  const currentDomain = extractDomain(window.location.href);
  // app reloaded from different domain - clear assistance
  if (referrerDomain !== currentDomain) {
    console.log(
      'App reloaded from different domain - clearing assistance and planNumber'
    );
    localStorage.removeItem('assistance');
    localStorage.removeItem('planNumber');
  } else {
    console.log('App reloaded from same domain');
  }
  return [
    importProvidersFrom(RecaptchaV3Module),
    provideAnimations(),
    provideAuth(),
    provideCatalogue(),
    provideCms(),
    provideCommonStore(),
    provideConfig(),
    provideEffects([]),
    provideFormsAddressDefaults(),
    provideI18nDefaults(),
    provideLightstepInitializer(),
    provideLogging({
      level: LoggingLevel.Error,
      silent: environment.production,
    }),
    provideNotificationsStore(),
    provideRecaptchaTokenInterceptor(),
    provideRouter(
      getAppRoutes(environment as any),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' })
    ),
    provideSelfFixStore(),
    provideSeo(),
    provideStore(
      {},
      {
        metaReducers: [
          sessionStorageSync({
            keys: [CATALOGUE_FEATURE_KEY, CMS_FEATURE_KEY, COMMON_FEATURE_KEY],
            rehydrate: true,
          }),
          resetMetaReducer(),
        ],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    !environment.production ? provideStoreDevtools() : [],
    {
      provide: APP_ID,
      useValue: 'dg-self-fix',
    },
    {
      provide: AUTH_CONFIG,
      useFactory: authConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: BYPASS_ROUTE_GUARD,
      useFactory: isInCmsEditorFactory,
      deps: [CMS_CONTEXT],
    },
    {
      provide: CATALOGUE_CONFIG,
      useFactory: catalogueConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: COMMON_CONFIG,
      useFactory: commonConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: CMS_CONTEXT,
      useFactory: (
        cmsConfig: CmsConfig,
        editorContextService: EditorContextService
      ) => cmsContextFactory()(cmsConfig.cmsRootPath, editorContextService),
      deps: [CMS_CONFIG, EditorContextService],
    },
    {
      provide: DOMAIN_ID,
      useFactory: domainIdFactory,
      deps: [CONFIG],
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: FORM_CONFIG,
      useFactory: formConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: GA_CONFIG,
      useValue: {},
    },
    provideHttpClient(
      withInterceptors([
        correlationIdInterceptor,
        formatPayloadInterceptor,
        gaHttpErrorInterceptor,
        loggingInterceptor,
        recaptchaTokenInterceptor,
        requestActionInterceptor,
        requestSourceInterceptor,
        whiteLabelClientInterceptor,
        myAccountInterceptor,
      ])
    ),
    {
      provide: LIGHTSTEP_CONFIG,
      useFactory: lightstepConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: RECAPTCHA_OVERRIDE_TOKEN,
      useFactory: recaptchaOverrideTokenFactory,
      deps: [CONFIG],
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: recaptchaSiteKeyFactory,
      deps: [CONFIG],
    },
    {
      provide: SELF_FIX_CONFIG,
      useFactory: selfFixConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: SEO_CONFIG,
      useFactory: seoConfigFactory,
      deps: [CONFIG],
    },
  ];
}
